import { onUnmounted, ref, watch } from 'vue';
import { SCChannel } from 'sc-channel';
import { AxiosPromise, AxiosResponse } from '@libs/http';
import { useQueryClient } from '@tanstack/vue-query';

import { useSocket } from './useSocket';
import { UseQueryConfigV2, useQueryV2 } from './useQueryV2';

export type UseLiveQueryConfigV2<T> = UseQueryConfigV2<T> & {
  fetcher: (...arg: any[]) => AxiosPromise<T>;
  channel?: string;
  unsubscribe?: boolean | ((channelName: string) => boolean);
  getChannels?: (data: AxiosResponse<T> | null) => Array<string> | void;
  getStaticChannels?: () => Array<string>;
  onUpdate: (signal: { code: number; data: any }) => void;
};

export function useLiveQueryV2<T>({
  fetcher,
  onUpdate,
  channel,
  unsubscribe = true,
  getChannels,
  getStaticChannels,
  ...options
}: UseLiveQueryConfigV2<T>): ReturnType<typeof useQueryV2<T>> {
  const channelsRef = ref<Record<string, SCChannel>>({});
  const { socket, onReady, connected } = useSocket();
  const queryClient = useQueryClient();

  function subscribe(channelNames?: string[] | void) {
    if (!channelNames?.length) return;

    onReady(() => {
      for (const name of channelNames) {
        if (socket.value && !channelsRef.value[name]) {
          const sub = socket.value.subscribe(name, { batch: true });

          channelsRef.value[name] = sub;

          if (typeof onUpdate === 'function') {
            sub.watch(onUpdate);
          }
        }
      }
    });
  }

  const query = useQueryV2<T>(fetcher, {
    ...options,
    onSuccess: (result) => {
      options.onSuccess?.(result);

      const dynamicChannels = channel ? [channel] : getChannels?.(result);

      subscribe(dynamicChannels);
    },
  });

  // TODO: This is currently applied here because it's only used/needed by the market depth composables.
  // In the future, we could extend useLiveInfiniteQuery and useLiveQuery to support early subscriptions directly.
  function subscribeToStaticChannels() {
    subscribe(getStaticChannels?.());
  }

  function handleUnsubscribe() {
    for (const [channelName, channel] of Object.entries(channelsRef.value)) {
      const shouldUnsubscribe =
        typeof unsubscribe === 'function'
          ? unsubscribe(channelName)
          : unsubscribe;

      if (shouldUnsubscribe) {
        channel.unsubscribe();
      }

      channel.unwatch(onUpdate);
      delete channelsRef.value[channelName];
    }
  }

  subscribeToStaticChannels();
  query.onKeyUpdated(() => {
    handleUnsubscribe();
    subscribeToStaticChannels();
  });

  onUnmounted(handleUnsubscribe);

  watch(connected, () => {
    if (!connected.value && !query.isRefetching.value) {
      queryClient.invalidateQueries(options.key);
    }
  });

  return { ...query };
}
